/**
 * @file InventoryItemFormModal.js
 * @version 1.13.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-13
 * @modified 2026-11-02
 * @description Create or edit an inventory item.
 *
 *              Extracted from SearchInventory, which had the only copy of this
 *              form hand-rolled inline with no way out — so anything else that
 *              needed to create an item (the order screen's Item picker) had to
 *              send the user away to another screen and abandon what they were
 *              doing.
 *
 *              The modal owns the form, its validation, the Xero conflict check
 *              and the save request. The CALLER owns what happens next, via
 *              onSaved(item, meta): Search Inventory refreshes its table and
 *              nudges about mineral pricing; the order picker selects the new
 *              item onto the line. On failure the modal stays open with the
 *              user's edits intact — the server pushes to Xero before writing
 *              anything locally, so a failure means nothing was saved on either
 *              side and Save retries the lot.
 *
 *              Markup deliberately mirrors the original portal rather than the
 *              shared Modal: SearchInventory.css styles `.modal-content
 *              .item-modal` and its header/section children directly, and
 *              swapping in Modal's header markup would regress all of it for no
 *              gain. The stylesheet import below is that dependency made
 *              explicit — every CSS import in this client lands in one global
 *              bundle, so it costs nothing but says what is going on.
 *
 *              The fields themselves, the blank form, validation and the
 *              customer/contract text check live in InventoryItemFields.js
 *              (2026-10-05), shared with the one-step "New formulation for a
 *              customer" so an item is collected the same way from both.
 *
 *              1.11.2 (2026-10-14): the conflict check and the save request
 *              moved to InventoryItemFields (checkItemConflict,
 *              saveItemRequest), unchanged, so the mineral window saves its item
 *              through the same request.
 *
 *              1.12.0 (2026-10-30, Cam): a changed item code asks first —
 *              ItemCodeChangeDialog lists the sales orders, POs, bills and
 *              invoices here that take the new code with it.
 *
 *              1.13.0 (2026-11-02, Cam): the item's Supplier (ItemSupplierField)
 *              — saved AFTER the item, through its own PUT, so the item's save
 *              and its Xero push never carry it (local only). The item saved
 *              and its supplier refused: the window stays open and says so.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import AuditHistoryModal from './AuditHistoryModal';
import ConflictResolutionModal from './ConflictResolutionModal';
import { useSlideOutButton } from './SlideOutButton';
// The fields, the blank form, validation and the text check are shared with the
// one-step customer formulation (NewCustomerFormulationModal), so an item is
// collected the same way from both.
import InventoryItemFields, {
  BLANK_ITEM_FORM, formFromItem, wantsFormulation, validateItemForm,
  checkItemText, textFieldsToCheck, useItemFormLists, ItemTextWarningDialog,
  pushesItemToXero, checkItemConflict, saveItemRequest, ItemCodeChangeDialog
} from './InventoryItemFields';
import useAccess from '../hooks/useAccess';
import FormulationModal from './FormulationModal';
import ItemSupplierField, { useItemSupplierField } from './ItemSupplierField';
import './SearchInventory.css';
import './InventoryItemFormModal.css';


/**
 * @param {boolean}  isOpen
 * @param {object?}  item      Existing item to edit; null/undefined to create.
 * @param {function} onClose
 * @param {function} onSaved   (savedItem, { isEditing, updatedInXero }) => void
 */
function InventoryItemFormModal({ isOpen, item = null, onClose, onSaved }) {
  const [formData, setFormData] = useState(BLANK_ITEM_FORM);
  const [initialFormData, setInitialFormData] = useState(null);
  /*
   * "This looks like a customer's or a contract's" — asked of the text before
   * saving (POST /api/inventory/text-check). An item is the product: a contract
   * number goes stale and shows on every document, and a customer's name or
   * farm in the name or PURCHASE description goes to every supplier. The sales
   * description may name the customer. On create every field is checked; on an
   * edit only what was changed, so an old item is not nagged about text it has
   * always had. The user can save anyway.
   */
  const [textWarning, setTextWarning] = useState(null); // { findings, args }
  const textAcceptedRef = useRef(false);
  // The code the person has seen the effect of (ItemCodeChangeDialog) — a
  // different code typed afterwards is asked about again.
  const codeConfirmedRef = useRef(null);
  const [codeChangePrompt, setCodeChangePrompt] = useState(null); // { plan, args }
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [conflictData, setConflictData] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  /*
   * A blend or compound that has just been created has no recipe yet, and the
   * moment somebody is most likely to write one is now — they have the item in
   * front of them. Offered, never assumed: answering "not now" does exactly
   * what saving did before.
   */
  const { can } = useAccess();
  const [formulationPrompt, setFormulationPrompt] = useState(null);
  const [formulationFor, setFormulationFor] = useState(null);
  const [formulationComponents, setFormulationComponents] = useState([]);

  const isEditing = !!item;
  // Who it is bought from (local only — saved after the item, never with it).
  const supplierField = useItemSupplierField({ isOpen, itemId: item?.id || null });

  // The modal renders its own slide-out buttons — Save, Cancel, and "Open in
  // Xero", which only exists once Tracked Inventory is ticked — so it measures
  // them itself. Relying on the host's measurement left the Xero button, added
  // after the host last measured, at a broken width.
  useSlideOutButton([isOpen, formData.is_tracked_as_inventory, saving]);

  // Seed the form each time the modal opens, so reopening after a cancel never
  // shows the previous item's values.
  useEffect(() => {
    if (!isOpen) return;
    if (item) {
      const seeded = formFromItem(item);
      setFormData(seeded);
      setInitialFormData({ ...seeded });
    } else {
      setFormData(BLANK_ITEM_FORM);
      setInitialFormData(null);
    }
    setFormErrors({});
  }, [isOpen, item]);

  // Account and tax-type lists, and an account's Xero default tax type for a
  // blank one (InventoryItemFields).
  const lists = useItemFormLists(isOpen, setFormData);

  const validateForm = () => {
    const errors = validateItemForm(formData);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = useCallback(async (resolvedConflicts = null, xeroValues = null) => {
    if (!validateForm()) {
      setAlertDialog({ isOpen: true, message: 'Please fill in all required fields', type: 'warning' });
      return;
    }

    if (!textAcceptedRef.current) {
      const findings = await checkItemText(textFieldsToCheck(formData, initialFormData, isEditing));
      if (findings.length) {
        setTextWarning({ findings, args: [resolvedConflicts, xeroValues] });
        return;
      }
    }

    const hasXeroId = isEditing && pushesItemToXero(item);

    // Editing a Xero-backed item: check whether Xero moved underneath us first.
    if (isEditing && hasXeroId && !resolvedConflicts) {
      const conflict = await checkItemConflict(item, formData, initialFormData);
      if (conflict) {
        setConflictData(conflict);
        setShowConflictModal(true);
        return; // Wait for the user to resolve before saving.
      }
    }

    setSaving(true);
    const result = await saveItemRequest({
      item: isEditing ? item : null, formData, resolvedConflicts, xeroValues,
      confirmCodeChange: !!codeConfirmedRef.current && codeConfirmedRef.current === formData.item_code
    });

    // A new code: say what it changes here first; nothing has been saved.
    if (!result.ok && result.codeChange) {
      setSaving(false);
      setCodeChangePrompt({ plan: result.codeChange, args: [resolvedConflicts, xeroValues] });
      return;
    }

    if (result.ok) {
      // The supplier, after the item (its own request: never part of the
      // item's save or its Xero push). Refused: the item stays saved and the
      // window open, saying so — Save again tries the supplier again.
      const supplier = await supplierField.save(result.item?.id || item?.id);
      if (!supplier.ok) {
        setSaving(false);
        setAlertDialog({ isOpen: true, message: `The item was saved, but its supplier was not: ${supplier.error}`, type: 'error' });
        return;
      }
      setSaving(false);
      /*
       * A NEW blend or compound has no recipe yet, and the moment somebody is
       * most likely to write one is right now, with the item in front of
       * them. Offered on create only — an edit is not the moment — and only
       * to somebody who could actually write it.
       *
       * "Not now" does exactly what saving did before, so the prompt costs a
       * keystroke and never a dead end.
       */
      if (!isEditing && result.item?.id && wantsFormulation(formData) && can('formulations.edit')) {
        setFormulationPrompt({ item: result.item, meta: { isEditing, updatedInXero: result.updatedInXero } });
        return;
      }
      onSaved?.(result.item, { isEditing, updatedInXero: result.updatedInXero });
      onClose();
      return;
    }

    // Stay open — nothing was written on either side, and the edits are still
    // on screen for Save to retry.
    setAlertDialog({ isOpen: true, message: result.message, type: 'error' });
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, initialFormData, isEditing, item, onSaved, onClose, can, supplierField]);

  if (!isOpen) return null;


  return (
    <>
      {ReactDOM.createPortal(
        <div className="modal-overlay" onClick={onClose}>
          <div className="modal-content item-modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="header-left">
                <h3>{isEditing ? 'Edit Item' : 'Add Item'}</h3>
              </div>
              <div className="header-actions">
                {isEditing && (
                  <button className="btn-icon" onClick={() => setShowHistory(true)} title="View History">
                    <span className="material-symbols-rounded">history</span>
                  </button>
                )}
                <button className="btn-icon close-btn" onClick={onClose} title="Close">
                  <span className="material-symbols-rounded">close</span>
                </button>
              </div>
            </div>

            <div className="modal-body">
              <InventoryItemFields
                formData={formData}
                setFormData={setFormData}
                formErrors={formErrors}
                setFormErrors={setFormErrors}
                lists={lists}
                item={item}
                onTextEdited={() => { textAcceptedRef.current = false; }}
              />
              <ItemSupplierField field={supplierField} />
            </div>

            <div className="modal-footer">
              <button data-read-safe onClick={onClose} className="btn-round btn-danger" disabled={saving}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Cancel</span>
              </button>
              <button onClick={() => handleSave()} className="btn-slideout" disabled={saving}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">save</span>
                </div>
                <span className="btn-label">{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {textWarning && (
        <ItemTextWarningDialog
          findings={textWarning.findings}
          onConfirm={() => {
            const [rc, xv] = textWarning.args;
            setTextWarning(null);
            textAcceptedRef.current = true;
            handleSave(rc, xv);
          }}
          onCancel={() => setTextWarning(null)}
        />
      )}

      {/* A new code: the lines that take it with them, before saving. */}
      {codeChangePrompt && (
        <ItemCodeChangeDialog
          plan={codeChangePrompt.plan}
          onConfirm={() => {
            const { args } = codeChangePrompt;
            codeConfirmedRef.current = formData.item_code;
            setCodeChangePrompt(null);
            handleSave(...args);
          }}
          onCancel={() => setCodeChangePrompt(null)}
        />
      )}

      {/* Raised, now give it a recipe? */}
      {formulationPrompt && !formulationFor && (
        <ConfirmDialog
          isOpen
          type="info"
          title="Add a formulation?"
          message={`${formulationPrompt.item.item_code} is a blend or compound and has no formulation yet. Write one now?`}
          confirmText="Add formulation"
          cancelText="Not now"
          onConfirm={async () => {
            // The component list the formulation editor works from. Fetched
            // here rather than held by this modal all the time: nothing else on
            // this screen needs it, and most items are not blends.
            try {
              const res = await fetch(`${API_URL}/api/formulations/components`, { credentials: 'include' });
              const data = await res.json();
              setFormulationComponents(data.components || data || []);
            } catch (e) {
              console.error('Could not load formulation components:', e);
              setFormulationComponents([]);
            }
            setFormulationFor(formulationPrompt.item);
          }}
          onCancel={() => {
            const { item: saved, meta } = formulationPrompt;
            setFormulationPrompt(null);
            onSaved?.(saved, meta);
            onClose();
          }}
        />
      )}

      {formulationFor && (
        <FormulationModal
          mode="add"
          initialItemId={formulationFor.id}
          components={formulationComponents}
          period={null}
          currentPeriod={null}
          onClose={() => {
            const saved = formulationFor;
            const meta = formulationPrompt?.meta || { isEditing: false };
            setFormulationFor(null);
            setFormulationPrompt(null);
            onSaved?.(saved, meta);
            onClose();
          }}
          onSaved={() => { /* handled by onClose above, which always runs */ }}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      {showHistory && isEditing && (
        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          entityType="inventory_item"
          entityId={item.id}
          entityIdentifier={`${item.item_code} - ${item.item_name}`}
        />
      )}

      <ConflictResolutionModal
        isOpen={showConflictModal}
        onClose={() => { setShowConflictModal(false); setConflictData(null); }}
        onResolve={({ resolutions, xeroValues }) => {
          setShowConflictModal(false);
          setConflictData(null);
          handleSave(resolutions, xeroValues);
        }}
        entityType="item"
        conflicts={conflictData?.conflicts || []}
        userChangedFields={conflictData?.userChangedFields || new Set()}
        xeroUpdatedAt={conflictData?.xeroUpdatedAt}
      />
    </>
  );
}

export default InventoryItemFormModal;
